/**
 * The account behind cloud sync.
 *
 * One account per deployment. The first person to reach a fresh Worker creates
 * it, and registration then closes -- there is no invitation flow to abuse and
 * no second user to escalate to.
 *
 * The session is held here rather than left to a cookie. The app is served from
 * `*.pages.dev` and the Worker from `*.workers.dev`, which are separate sites,
 * and Safari does not return cookies across that boundary. Storing the session
 * ourselves and sending it as a bearer token is what makes sync work on the
 * device this was built for.
 *
 * This password protects the synchronised copy of the ledger and nothing else.
 * No bank login, PIN, OTP or Secure2u approval is ever requested or stored.
 */
import { db, readMeta, writeMeta, type WhatISpentDatabase } from '../db/database.js';

const SESSION_KEY = 'sync.sessionId';

export interface DeploymentStatus {
  needsRegistration: boolean;
  ingestConfigured: boolean;
}

export class AccountError extends Error {}

export async function readSessionId(
  database: WhatISpentDatabase = db,
): Promise<string | null> {
  return (await readMeta<string>(SESSION_KEY, database)) ?? null;
}

export async function writeSessionId(
  value: string | null,
  database: WhatISpentDatabase = db,
): Promise<void> {
  await writeMeta(SESSION_KEY, value, database);
}

function endpoint(baseUrl: string, path: string): string {
  const trimmed = baseUrl.trim().replace(/\/$/, '');
  if (!trimmed) throw new AccountError('Enter the address of your Worker first.');
  return `${trimmed}${path}`;
}

/**
 * Turn any failure into a sentence worth reading.
 *
 * The Worker never puts submitted values in an error, so whatever comes back is
 * safe to show; when it says nothing useful, the status code is translated
 * rather than printed.
 */
async function explain(response: Response): Promise<never> {
  let message: string | undefined;
  try {
    const body = (await response.json()) as { error?: { message?: string } };
    message = body.error?.message;
  } catch {
    // An empty or non-JSON body is itself unremarkable; fall through.
  }
  if (!message) {
    message =
      response.status === 401
        ? 'Email or password is incorrect.'
        : response.status === 429
          ? 'Too many attempts. Wait a minute and try again.'
          : `The Worker returned ${response.status}.`;
  }
  throw new AccountError(message);
}

async function post(
  baseUrl: string,
  path: string,
  body: unknown,
): Promise<{ sessionId?: string }> {
  let response: Response;
  try {
    response = await fetch(endpoint(baseUrl, path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new AccountError('Could not reach that address. Check it and your connection.');
  }
  if (!response.ok) await explain(response);
  return (await response.json()) as { sessionId?: string };
}

/** Does this deployment still need its one account, and is ingestion set up? */
export async function readDeploymentStatus(baseUrl: string): Promise<DeploymentStatus> {
  let response: Response;
  try {
    response = await fetch(endpoint(baseUrl, '/api/auth/status'));
  } catch {
    throw new AccountError('Could not reach that address. Check it and your connection.');
  }
  if (!response.ok) await explain(response);
  return (await response.json()) as DeploymentStatus;
}

/** True when the stored session is still accepted by the Worker. */
export async function isSignedIn(
  baseUrl: string,
  database: WhatISpentDatabase = db,
): Promise<boolean> {
  const sessionId = await readSessionId(database);
  if (!sessionId) return false;
  try {
    const response = await fetch(endpoint(baseUrl, '/api/auth/me'), {
      headers: { Authorization: `Bearer ${sessionId}` },
    });
    return response.ok;
  } catch {
    // Offline is not signed out: the session may well still be good.
    throw new AccountError('Could not reach that address. Check it and your connection.');
  }
}

export async function registerAccount(
  baseUrl: string,
  email: string,
  password: string,
  database: WhatISpentDatabase = db,
): Promise<void> {
  const result = await post(baseUrl, '/api/auth/register', { email, password });
  if (!result.sessionId) throw new AccountError('The Worker did not return a session.');
  await writeSessionId(result.sessionId, database);
}

export async function signIn(
  baseUrl: string,
  email: string,
  password: string,
  database: WhatISpentDatabase = db,
): Promise<void> {
  const result = await post(baseUrl, '/api/auth/login', { email, password });
  if (!result.sessionId) throw new AccountError('The Worker did not return a session.');
  await writeSessionId(result.sessionId, database);
}

/**
 * Sign out.
 *
 * The local session is cleared whatever the Worker says. A revocation that
 * fails to send must not leave this device holding a token it believes is gone;
 * the server copy expires on its own.
 */
export async function signOut(
  baseUrl: string,
  database: WhatISpentDatabase = db,
): Promise<void> {
  const sessionId = await readSessionId(database);
  if (sessionId) {
    try {
      await fetch(endpoint(baseUrl, '/api/auth/logout'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${sessionId}` },
      });
    } catch {
      // See above.
    }
  }
  await writeSessionId(null, database);
}
